import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  useCreateBlockNote,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  DefaultReactSuggestionItem,
  SuggestionMenuProps,
} from "@blocknote/react";
import { BlockNoteSchema, defaultBlockSpecs } from "@blocknote/core";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { FiltersContext } from "@/models/filter";
import {
  WIDGET_REGISTRY,
  WIDGET_TYPES,
  BackendWidget,
  getBackendWidgetType,
} from "@/models/widget-v2";
import { useDashboards } from "@/api/dashboard";
import { useWidgets } from "@/api/widget";
import { Dashboard } from "@/models/dashboard";
import { WidgetBlock } from "./blocknote-widget-block";
import { DashboardBlock } from "./blocknote-dashboard-block";
import { TextWidgetFiltersContext } from "./text-widget-context";
import {
  FaChartBar,
  FaTachometerAlt,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";

// Fonction de filtrage des suggestions (réimplémentée pour éviter les problèmes d'import)
function filterSuggestionItems(
  items: DefaultReactSuggestionItem[],
  query: string,
): DefaultReactSuggestionItem[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return items;

  return items.filter((item) => {
    const titleMatch = item.title.toLowerCase().includes(normalizedQuery);
    const aliasMatch = item.aliases?.some((alias: string) =>
      alias.toLowerCase().includes(normalizedQuery),
    );
    return titleMatch || aliasMatch;
  });
}

// Schema BlockNote personnalisé avec les blocs widget et dashboard
const schema = BlockNoteSchema.create({
  blockSpecs: {
    ...defaultBlockSpecs,
    widget: WidgetBlock(),
    dashboard: DashboardBlock(),
  },
});

// Types de widgets à exclure du menu (le widget text lui-même ne doit pas apparaître dans son propre menu)
const EXCLUDED_WIDGET_TYPES: string[] = [WIDGET_TYPES.TEXT];

// ============================================================================
// @ Mention Menu Types and Configuration
// ============================================================================

type MentionItemType = "widget" | "dashboard";

interface MentionItem extends DefaultReactSuggestionItem {
  key?: string;
  itemType: MentionItemType;
  widget?: BackendWidget;
  dashboard?: Dashboard;
}

// ============================================================================
// Custom @ Menu Component (Spotlight-style)
// ============================================================================

interface CustomMentionMenuProps extends SuggestionMenuProps<MentionItem> {
  editor: typeof schema.BlockNoteEditor;
}

const CustomMentionMenu: React.FC<CustomMentionMenuProps> = ({
  items,
  selectedIndex,
  onItemClick,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<MentionItemType>>(
    new Set(),
  );

  // Group items by type
  const groupedItems = useMemo(() => {
    const groups: Record<MentionItemType, MentionItem[]> = {
      widget: [],
      dashboard: [],
    };
    items.forEach((item) => {
      groups[item.itemType].push(item);
    });
    return groups;
  }, [items]);

  // Build navigation structure
  const typeOrder: MentionItemType[] = ["widget", "dashboard"];
  const sortedGroups = typeOrder
    .filter((type) => groupedItems[type]?.length > 0)
    .map(
      (type) => [type, groupedItems[type]] as [MentionItemType, MentionItem[]],
    );

  // Calculate global indices for each item
  const itemToGlobalIndex = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    sortedGroups.forEach(([type, typeItems]) => {
      if (!collapsedGroups.has(type)) {
        typeItems.forEach((item) => {
          map.set(item.key || item.title, idx);
          idx++;
        });
      } else {
        idx += typeItems.length;
      }
    });
    return map;
  }, [sortedGroups, collapsedGroups]);

  // Scroll selected item into view
  useEffect(() => {
    const el =
      selectedIndex !== undefined ? itemRefs.current.get(selectedIndex) : null;
    if (el && listRef.current) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  const toggleGroup = (type: MentionItemType) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const getGroupConfig = (type: MentionItemType) => {
    switch (type) {
      case "widget":
        return {
          label: "Widgets",
          icon: <FaChartBar size={14} />,
          color: "text-blue-400",
          bgColor: "bg-blue-500/10",
        };
      case "dashboard":
        return {
          label: "Dashboards",
          icon: <FaTachometerAlt size={14} />,
          color: "text-purple-400",
          bgColor: "bg-purple-500/10",
        };
    }
  };

  if (items.length === 0) {
    return (
      <div className="bg-surface-elevated rounded-lg shadow-xl border border-arc-border overflow-hidden min-w-[320px] z-[10001]">
        <div className="px-4 py-6 text-center text-content-tertiary text-sm">
          No widgets or dashboards found
        </div>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="bg-surface-elevated rounded-lg shadow-xl border border-arc-border overflow-hidden min-w-[320px] max-w-[400px] max-h-[400px] overflow-y-auto z-[10001]"
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-arc-border bg-surface-secondary">
        <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">
          Insert Widget or Dashboard
        </span>
      </div>

      {/* Groups */}
      <div className="py-1">
        {sortedGroups.map(([type, typeItems]) => {
          const config = getGroupConfig(type);
          const isCollapsed = collapsedGroups.has(type);

          return (
            <div key={type} className="mb-0.5">
              {/* Group header */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleGroup(type)}
                className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-surface-secondary cursor-pointer transition-colors"
              >
                <div className="text-content-tertiary">
                  {isCollapsed ? (
                    <FaChevronRight className="h-2.5 w-2.5" />
                  ) : (
                    <FaChevronDown className="h-2.5 w-2.5" />
                  )}
                </div>
                <div
                  className={`${config.bgColor} ${config.color} p-1 rounded`}
                >
                  {config.icon}
                </div>
                <span className="text-xs font-semibold text-content-secondary flex-1 text-left">
                  {config.label}
                </span>
                <span className="text-xs tabular-nums px-1.5 py-0.5 rounded-full bg-surface-tertiary text-content-tertiary">
                  {typeItems.length}
                </span>
              </div>

              {/* Items */}
              {!isCollapsed && (
                <div>
                  {typeItems.map((item) => {
                    const globalIndex =
                      itemToGlobalIndex.get(item.key || item.title) ?? -1;
                    const isSelected = globalIndex === selectedIndex;

                    return (
                      <div
                        key={item.key || item.title}
                        ref={(el) => {
                          if (el) itemRefs.current.set(globalIndex, el);
                        }}
                        className={`
                          flex items-center gap-3 pl-8 pr-3 py-2 cursor-pointer transition-colors
                          ${isSelected ? "bg-accent/20" : "hover:bg-surface-secondary"}
                        `}
                        onClick={() => onItemClick?.(item)}
                      >
                        {/* Icon */}
                        <div
                          className={`
                            w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0
                            ${isSelected ? "bg-accent/20 text-accent" : "bg-surface-tertiary text-content-secondary"}
                          `}
                        >
                          {item.itemType === "widget" && item.widget?.chart ? (
                            <span className="text-base">
                              {WIDGET_REGISTRY[item.widget.chart]?.icon || "📊"}
                            </span>
                          ) : item.itemType === "dashboard" &&
                            item.dashboard?.icon ? (
                            <span className="text-base">
                              {item.dashboard.icon}
                            </span>
                          ) : item.itemType === "dashboard" ? (
                            <span className="text-base">📊</span>
                          ) : (
                            <FaChartBar size={14} />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-sm font-medium truncate ${
                              isSelected
                                ? "text-accent"
                                : "text-content-primary"
                            }`}
                          >
                            {item.title}
                          </div>
                          {item.subtext && (
                            <div className="text-xs text-content-tertiary truncate">
                              {item.subtext}
                            </div>
                          )}
                        </div>

                        {/* Selection indicator */}
                        {isSelected && (
                          <span className="text-xs text-content-tertiary flex-shrink-0">
                            ↵
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-arc-border bg-surface-secondary flex items-center gap-3 text-[10px] text-content-tertiary">
        <span className="flex items-center gap-1">
          <kbd className="px-1 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
            ↑↓
          </kbd>
          navigate
        </span>
        <span className="flex items-center gap-1">
          <kbd className="px-1 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
            ↵
          </kbd>
          insert
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// Menu Items Generators
// ============================================================================

// Get widget name with fallback
const getWidgetName = (widget: BackendWidget): string => {
  return (
    widget.name ||
    widget.metadata?.name ||
    `Widget ${getBackendWidgetType(widget, "unknown" as any)}`
  );
};

// Get widget description based on type
const getWidgetDescription = (widget: BackendWidget): string => {
  const chartType = getBackendWidgetType(widget);
  const meta = chartType
    ? WIDGET_REGISTRY[chartType as keyof typeof WIDGET_REGISTRY]
    : null;
  return meta?.name || chartType || "";
};

// Helper pour vérifier si un bloc est vide ou contient seulement le trigger "@"
const isBlockEmptyOrTrigger = (block: any): boolean => {
  if (!block.content || !Array.isArray(block.content)) return true;
  if (block.content.length === 0) return true;
  // Vérifier si le contenu est juste le trigger "@" ou vide
  const textContent = block.content
    .map((c: any) => (typeof c === "string" ? c : c.text || ""))
    .join("")
    .trim();
  return textContent === "" || textContent === "@";
};

// Créer les items de menu @ pour les widgets existants
const getWidgetMentionItems = (
  editor: typeof schema.BlockNoteEditor,
  widgets: BackendWidget[],
): MentionItem[] => {
  return widgets
    .filter(
      (widget) => !EXCLUDED_WIDGET_TYPES.includes(getBackendWidgetType(widget)),
    )
    .map((widget) => {
      const chartType = getBackendWidgetType(widget);
      return {
        key: `widget-${widget.id}`,
        title: getWidgetName(widget),
        itemType: "widget" as const,
        widget,
        onItemClick: () => {
          const currentBlock = editor.getTextCursorPosition().block;
          const newBlock = {
            type: "widget" as const,
            props: {
              widgetType: chartType,
              widgetName: getWidgetName(widget),
              widgetConfig: JSON.stringify(widget),
              height: (widget.h || 4) * 80,
            },
          } as any;

          // Si le bloc courant est vide, le remplacer; sinon insérer après
          if (isBlockEmptyOrTrigger(currentBlock)) {
            editor.replaceBlocks([currentBlock], [newBlock]);
          } else {
            editor.insertBlocks([newBlock], currentBlock, "after");
          }
        },
        aliases: [
          "widget",
          chartType || "",
          getWidgetName(widget).toLowerCase(),
          ...getWidgetName(widget).toLowerCase().split(" "),
        ].filter(Boolean),
        group: "Widgets",
        icon: (
          <span style={{ fontSize: "18px" }}>
            {chartType
              ? WIDGET_REGISTRY[chartType as keyof typeof WIDGET_REGISTRY]
                  ?.icon || "📊"
              : "📊"}
          </span>
        ),
        subtext: getWidgetDescription(widget),
      };
    });
};

// Créer les items de menu @ pour les dashboards existants
const getDashboardMentionItems = (
  editor: typeof schema.BlockNoteEditor,
  dashboards: Dashboard[],
): MentionItem[] => {
  return dashboards.map((dashboard) => ({
    key: `dashboard-${dashboard.id}`,
    title:
      dashboard.name ||
      String(dashboard.metadata?.name ?? "") ||
      "Untitled Dashboard",
    itemType: "dashboard",
    dashboard,
    onItemClick: () => {
      const currentBlock = editor.getTextCursorPosition().block;
      const newBlock = {
        type: "dashboard" as const,
        props: {
          dashboardId: dashboard.id,
          height: 400,
        },
      } as any;

      // Si le bloc courant est vide, le remplacer; sinon insérer après
      if (isBlockEmptyOrTrigger(currentBlock)) {
        editor.replaceBlocks([currentBlock], [newBlock]);
      } else {
        editor.insertBlocks([newBlock], currentBlock, "after");
      }
    },
    aliases: [
      "dashboard",
      dashboard.name?.toLowerCase() || "",
      typeof dashboard.metadata?.name === "string"
        ? dashboard.metadata.name.toLowerCase()
        : "",
    ].filter(Boolean),
    group: "Dashboards",
    icon: <span style={{ fontSize: "18px" }}>{dashboard.icon || "📊"}</span>,
    subtext: dashboard.description || "",
  }));
};

// Fonction pour obtenir les items du slash menu (par défaut seulement, sans widgets/dashboards)
const getCustomSlashMenuItems = (
  editor: typeof schema.BlockNoteEditor,
): DefaultReactSuggestionItem[] => [...getDefaultReactSlashMenuItems(editor)];

// Type pour les blocs du schema personnalisé
type CustomBlock = typeof schema.Block;

interface TextWidgetProps {
  widgetName: string;
  config: any;
  filtersCtx: FiltersContext;
  data?: any;
  onChange?: (blocks: CustomBlock[]) => void;
}

const TextWidget: React.FC<TextWidgetProps> = ({
  config,
  filtersCtx,
  onChange,
}) => {
  const textConfig = config?.chartConfig || config?.visual || config;

  // Fetch existing resources for the @ menu
  const { data: dashboards = [] } = useDashboards();
  const { data: widgets = [] } = useWidgets();

  // Detect dark mode from the theme class on document root
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document !== "undefined") {
      return document.documentElement.classList.contains("theme-night");
    }
    return false;
  });

  // Listen for theme changes
  useEffect(() => {
    if (typeof document === "undefined") return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDarkMode(
            document.documentElement.classList.contains("theme-night"),
          );
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // Récupérer les blocs depuis la config
  const initialBlocks = useMemo(() => {
    // Essayer plusieurs chemins possibles pour les blocks
    const blocks =
      textConfig?.general?.blocks || textConfig?.blocks || config?.blocks;
    if (blocks && Array.isArray(blocks) && blocks.length > 0) {
      return blocks;
    }
    return undefined; // BlockNote créera un bloc paragraphe vide par défaut
  }, [textConfig?.general?.blocks, textConfig?.blocks, config?.blocks]);

  // Options visuelles
  // Par défaut, le fond est transparent sauf si explicitement désactivé (transparentBackground === false)
  const transparentBgValue = textConfig?.transparentBackground;
  const isTransparent = transparentBgValue !== false;
  const backgroundColor = isTransparent
    ? "transparent"
    : textConfig?.backgroundColor ||
      (isDarkMode ? "var(--arc-surface)" : "#ffffff");
  const padding = textConfig?.padding ?? 8;

  // Créer l'éditeur BlockNote avec le schema personnalisé
  const editor = useCreateBlockNote({
    schema,
    initialContent: initialBlocks,
  });

  // Get @ mention menu items (existing widgets + existing dashboards)
  const getMentionItems = useCallback(
    (query: string): MentionItem[] => {
      const widgetItems = getWidgetMentionItems(editor, widgets);
      const dashboardItems = getDashboardMentionItems(editor, dashboards);
      const allItems = [...widgetItems, ...dashboardItems];
      return filterSuggestionItems(allItems, query) as MentionItem[];
    },
    [editor, widgets, dashboards],
  );

  // Notifier le parent des changements
  React.useEffect(() => {
    if (!onChange) return;

    const unsubscribe = editor.onEditorContentChange(() => {
      onChange(editor.document as CustomBlock[]);
    });

    return unsubscribe;
  }, [editor, onChange]);

  return (
    <TextWidgetFiltersContext.Provider value={filtersCtx}>
      <div
        className={`h-full w-full overflow-auto ${isTransparent ? "blocknote-transparent" : ""}`}
        style={{
          backgroundColor,
          padding,
        }}
      >
        <style>{`
          .blocknote-transparent .bn-container,
          .blocknote-transparent .bn-editor,
          .blocknote-transparent .ProseMirror {
            background: transparent !important;
            background-color: transparent !important;
          }
        `}</style>
        <BlockNoteView
          editor={editor}
          theme={isDarkMode ? "dark" : "light"}
          slashMenu={false}
        >
          {/* Default slash menu for basic blocks */}
          <SuggestionMenuController
            triggerCharacter="/"
            getItems={async (query) =>
              filterSuggestionItems(getCustomSlashMenuItems(editor), query)
            }
          />
          {/* @ mention menu for widgets and dashboards */}
          <SuggestionMenuController
            triggerCharacter="@"
            getItems={async (query) => getMentionItems(query)}
            suggestionMenuComponent={
              ((props: any) => (
                <CustomMentionMenu {...props} editor={editor} />
              )) as any
            }
          />
        </BlockNoteView>
      </div>
    </TextWidgetFiltersContext.Provider>
  );
};

export default TextWidget;
